import {
  getObject,
  getLeoUuid,
  LeoUUID,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { RespondentSelection } from "../types/respondentSelection";
/* eslint-disable import/export */
export abstract class ReopenSurveyLinksRPC implements
  LeoRPC<ReopenSurveyLinksRPC.Request, ReopenSurveyLinksRPC.Response, ReopenSurveyLinksRPC.Errors.Errors>
{
  abstract execute(
    request: ReopenSurveyLinksRPC.Request
  ): Promise<LeoRPCResult<ReopenSurveyLinksRPC.Response, ReopenSurveyLinksRPC.Errors.Errors>>;
}

export namespace ReopenSurveyLinksRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    respondentSelection: RespondentSelection.RespondentSelectionDTO;
    projectId: string;
    surveyId: string;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isReopenSurveyLinksRPCRequest: boolean = true;
  
    readonly respondentSelection: RespondentSelection.RespondentSelection;
    readonly projectId: LeoUUID;
    readonly surveyId: LeoUUID;
  
  
    constructor(
    respondentSelection: RespondentSelection.RespondentSelection, 
    projectId: LeoUUID, 
    surveyId: LeoUUID
    ) {
      this.respondentSelection = respondentSelection;
      this.projectId = projectId;
      this.surveyId = surveyId;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const respondentSelection = RespondentSelection.fromDTO(getObject(dto, "respondentSelection", InvalidRequestRespondentSelectionError));
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      const surveyId = getLeoUuid(dto, "surveyId", InvalidRequestSurveyIdError);
      return new Request(
        respondentSelection,
        projectId,
        surveyId
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          respondentSelection: this.respondentSelection.toDTO(),
          projectId: this.projectId.toDTO(),
          surveyId: this.surveyId.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `ReopenSurveyLinksRPC(respondentSelection='${this.respondentSelection}',projectId='${this.projectId}',surveyId='${this.surveyId}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {}

  export class Response implements LeoRPCResponse {
    static fromDTO(): Response {
      return new Response();
    }

    toDTO(): ResponseDTO {
      return {}
    }
  }

  Response.prototype.toString = function toString(): string {
    return `ReopenSurveyLinksRPC.Response()`;
  };

  export enum RPCError {
    InvalidProjectId = "INVALID_PROJECT_ID",
    ProjectAlreadyArchived = "PROJECT_ALREADY_ARCHIVED",
    InvalidSurveyId = "INVALID_SURVEY_ID",
    DeletedColumnFilter = "DELETED_COLUMN_FILTER",
    InvalidRespondentIdFound = "INVALID_RESPONDENT_ID_FOUND",
    SurveyIsClosed = "SURVEY_IS_CLOSED"
  }

  export namespace Errors {
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `ReopenSurveyLinksRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface ProjectAlreadyArchivedDTO {
      code: string;
    }
    
    export class ProjectAlreadyArchived extends LeoRPCError<RPCError> {
      readonly isProjectAlreadyArchived: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectAlreadyArchived;
        super(code);
      }
    
    
      toDTO(): ProjectAlreadyArchivedDTO {
        const dto: ProjectAlreadyArchivedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectAlreadyArchived.prototype.toString = function toString(): string {
      return `ReopenSurveyLinksRPC.PROJECT_ALREADY_ARCHIVED()`;
    };
    
    
    export interface InvalidSurveyIdDTO {
      code: string;
    }
    
    export class InvalidSurveyId extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyId;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyIdDTO {
        const dto: InvalidSurveyIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyId.prototype.toString = function toString(): string {
      return `ReopenSurveyLinksRPC.INVALID_SURVEY_ID()`;
    };
    
    
    export interface DeletedColumnFilterDTO {
      code: string;
    }
    
    export class DeletedColumnFilter extends LeoRPCError<RPCError> {
      readonly isDeletedColumnFilter: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.DeletedColumnFilter;
        super(code);
      }
    
    
      toDTO(): DeletedColumnFilterDTO {
        const dto: DeletedColumnFilterDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    DeletedColumnFilter.prototype.toString = function toString(): string {
      return `ReopenSurveyLinksRPC.DELETED_COLUMN_FILTER()`;
    };
    
    
    export interface InvalidRespondentIdFoundDTO {
      code: string;
    }
    
    export class InvalidRespondentIdFound extends LeoRPCError<RPCError> {
      readonly isInvalidRespondentIdFound: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidRespondentIdFound;
        super(code);
      }
    
    
      toDTO(): InvalidRespondentIdFoundDTO {
        const dto: InvalidRespondentIdFoundDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidRespondentIdFound.prototype.toString = function toString(): string {
      return `ReopenSurveyLinksRPC.INVALID_RESPONDENT_ID_FOUND()`;
    };
    
    
    export interface SurveyIsClosedDTO {
      code: string;
    }
    
    export class SurveyIsClosed extends LeoRPCError<RPCError> {
      readonly isSurveyIsClosed: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.SurveyIsClosed;
        super(code);
      }
    
    
      toDTO(): SurveyIsClosedDTO {
        const dto: SurveyIsClosedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    SurveyIsClosed.prototype.toString = function toString(): string {
      return `ReopenSurveyLinksRPC.SURVEY_IS_CLOSED()`;
    };
    

    export type Errors = InvalidProjectId | ProjectAlreadyArchived | InvalidSurveyId | DeletedColumnFilter | InvalidRespondentIdFound | SurveyIsClosed;
    export type ErrorsDTO = InvalidProjectIdDTO | ProjectAlreadyArchivedDTO | InvalidSurveyIdDTO | DeletedColumnFilterDTO | InvalidRespondentIdFoundDTO | SurveyIsClosedDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.ProjectAlreadyArchived:
          return new ProjectAlreadyArchived();
        case RPCError.InvalidSurveyId:
          return new InvalidSurveyId();
        case RPCError.DeletedColumnFilter:
          return new DeletedColumnFilter();
        case RPCError.InvalidRespondentIdFound:
          return new InvalidRespondentIdFound();
        case RPCError.SurveyIsClosed:
          return new SurveyIsClosed();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestRespondentSelectionError extends ReopenSurveyLinksRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestRespondentSelectionError";
  }
}

export class InvalidRequestProjectIdError extends ReopenSurveyLinksRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidRequestSurveyIdError extends ReopenSurveyLinksRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyIdError";
  }
}


export class InvalidResponseInvalidProjectIdError extends ReopenSurveyLinksRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseProjectAlreadyArchivedError extends ReopenSurveyLinksRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectAlreadyArchivedError";
  }
}

export class InvalidResponseInvalidSurveyIdError extends ReopenSurveyLinksRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyIdError";
  }
}

export class InvalidResponseDeletedColumnFilterError extends ReopenSurveyLinksRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseDeletedColumnFilterError";
  }
}

export class InvalidResponseInvalidRespondentIdFoundError extends ReopenSurveyLinksRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidRespondentIdFoundError";
  }
}

export class InvalidResponseSurveyIsClosedError extends ReopenSurveyLinksRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyIsClosedError";
  }
}

